/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';

import { cn } from '@/lib/utils';

/**
 * Unstyled-ish shadcn ToggleGroup over Radix ToggleGroup (CRCB2B-642).
 *
 * This is the plain primitive. The segmented-button appearance lives in
 * `ui/segmented-button-styles.ts` and is applied by `ui/segmented-button.tsx`.
 *
 * ## ARIA output, read from the installed `1.1.19` source
 *
 * `type="single"` renders the root as **`role="radiogroup"`** and each item as
 * **`role="radio"` with `aria-checked`**, explicitly setting `aria-pressed` to `undefined`
 * (`dist/index.mjs:24,151`). `type="multiple"` is the other branch: `role="toolbar"` with
 * `aria-pressed` items. Roving focus is on by default.
 *
 * Two consequences worth knowing before writing assertions or a consumer:
 *
 * - Assert `role="radiogroup"` / `role="radio"` / `aria-checked`, never `aria-pressed`.
 * - **A `radiogroup` needs an accessible name.** Always pass `aria-label` or
 *   `aria-labelledby` on the root. An unnamed group announces its options with no idea what
 *   they are choosing between.
 */
function ToggleGroup({
    className,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
    return (
        <ToggleGroupPrimitive.Root
            data-slot="toggle-group"
            className={cn('flex items-center', className)}
            {...props}
        />
    );
}

function ToggleGroupItem({
    className,
    ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
    return (
        <ToggleGroupPrimitive.Item
            data-slot="toggle-group-item"
            className={cn('inline-flex items-center justify-center outline-none', className)}
            {...props}
        />
    );
}

export { ToggleGroup, ToggleGroupItem };
